/**
 * Express Enroll Program Utilities
 *
 * Handles program-specific logic for Express Enroll:
 *
 * Lilly Verzenio:
 * - Sorting: Lilly Savings Card before Lilly Ongoing Support
 * - Confirmation: Show warning when selecting Ongoing Support if Savings Card is available
 *
 * Teva Austedo:
 * - Sorting: Austedo Copay Card before Austedo Support Program
 * - Confirmation: Show warning when selecting Support Program if Copay Card is available
 */

import React from "react";
import { Modal } from "antd";
import { ReactComponent as WarningIcon } from "../../../../assets/svg/warning-icon-popup.svg";

// ============================================================================
// Lilly Verzenio Program Sorting & Confirmation
// ============================================================================

// Lilly Verzenio program PIDs
export const LILLY_SAVINGS_CARD_PID = "Verzenio Savings Card";
export const LILLY_ONGOING_SUPPORT_PID = "Verzenio Ongoing Support";

// Teva Austedo program PIDs
export const TEVA_COPAY_CARD_PID = "Austedo Copay Card - Teva Neuroscience";
export const TEVA_SUPPORT_PROGRAM_PID = "Austedo Teva Total Support";

/**
 * Gets the program PID from a potential saving or program object.
 * @param {Object} item - Potential saving or program object
 * @returns {string|null} Program PID or null
 */
const getProgramPid = (item) => {
  return item?.assistanceProgramPid || item?.pid || null;
};

/**
 * Sorts Lilly Verzenio programs so Savings Card appears before Ongoing Support.
 */
const sortLillyPrograms = (items) => {
  const result = [...items];

  const savingsCardIndex = result.findIndex((item) => getProgramPid(item) === LILLY_SAVINGS_CARD_PID);
  const ongoingSupportIndex = result.findIndex((item) => getProgramPid(item) === LILLY_ONGOING_SUPPORT_PID);

  if (savingsCardIndex !== -1 && ongoingSupportIndex !== -1 && ongoingSupportIndex < savingsCardIndex) {
    const temp = result[savingsCardIndex];
    result[savingsCardIndex] = result[ongoingSupportIndex];
    result[ongoingSupportIndex] = temp;
  }

  return result;
};

/**
 * Sorts Teva Austedo programs so Copay Card appears before Support Program.
 */
const sortTevaPrograms = (items) => {
  const result = [...items];

  const copayIndex = result.findIndex((item) => getProgramPid(item) === TEVA_COPAY_CARD_PID);
  const supportIndex = result.findIndex((item) => getProgramPid(item) === TEVA_SUPPORT_PROGRAM_PID);

  if (copayIndex !== -1 && supportIndex !== -1 && supportIndex < copayIndex) {
    const temp = result[copayIndex];
    result[copayIndex] = result[supportIndex];
    result[supportIndex] = temp;
  }

  return result;
};

/**
 * Sorts Express Enroll programs according to program-specific rules.
 * Add new sorters here as needed.
 *
 * @param {Array} items - Array of potential savings
 * @returns {Array} Sorted array (new array, original not mutated)
 */
export const sortExpressEnrollPrograms = (items) => {
  if (!items?.length) return [];

  let result = items;
  result = sortLillyPrograms(result);
  result = sortTevaPrograms(result);

  return result;
};

/**
 * Program confirmation rules.
 * Each rule defines when a confirmation modal should be shown before enrollment.
 */
const PROGRAM_CONFIRMATION_RULES = [
  {
    triggerPid: LILLY_ONGOING_SUPPORT_PID,
    requiredPeerPid: LILLY_SAVINGS_CARD_PID,
    titleKey: "patientPotentialSavings.verzenio_confirmation_title",
    messageKey: "patientPotentialSavings.verzenio_confirmation_message",
    continueKey: "patientPotentialSavings.verzenio_continue_anyway"
  },
  {
    triggerPid: TEVA_SUPPORT_PROGRAM_PID,
    requiredPeerPid: TEVA_COPAY_CARD_PID,
    titleKey: "patientPotentialSavings.austedo_confirmation_title",
    messageKey: "patientPotentialSavings.austedo_confirmation_message",
    continueKey: "patientPotentialSavings.austedo_continue_anyway"
  }
];

/**
 * Checks if selecting a program requires user confirmation.
 * Returns confirmation config if user is selecting a support-only program
 * when a copay program is also available.
 *
 * The button props are configured to make Cancel more prominent (primary/blue)
 * and Continue less prominent (secondary/default), encouraging users to
 * reconsider and potentially choose the copay program instead.
 *
 * @param {Object} currentProgram - The program being selected (assistanceProgram object)
 * @param {Array} allPrograms - All available programs (potentialSavings)
 * @returns {Object|null} Confirmation config with translation keys and button props, or null
 */
export const getExpressEnrollConfirmation = (currentProgram, allPrograms) => {
  if (!currentProgram || !allPrograms?.length) return null;

  const currentPid = getProgramPid(currentProgram);

  const matchedRule = PROGRAM_CONFIRMATION_RULES.find(
    (rule) => rule.triggerPid === currentPid && allPrograms.some((p) => getProgramPid(p) === rule.requiredPeerPid)
  );

  if (!matchedRule) return null;

  return {
    titleKey: matchedRule.titleKey,
    messageKey: matchedRule.messageKey,
    continueKey: matchedRule.continueKey,
    okButtonProps: { type: "default" },
    cancelButtonProps: { type: "primary" },
    swapButtonOrder: true
  };
};

/**
 * Shows Express Enroll confirmation modal if needed, otherwise proceeds directly.
 *
 * @param {Object|null} confirmation - Confirmation config from getExpressEnrollConfirmation, or null
 * @param {Function} t - Translation function
 * @param {Function} onProceed - Callback to execute when user confirms or if no confirmation needed
 */
export const showExpressEnrollConfirmation = (confirmation, t, onProceed) => {
  if (!confirmation) {
    onProceed();
    return;
  }

  Modal.confirm({
    title: t(confirmation.titleKey),
    content: t(confirmation.messageKey),
    okText: t(confirmation.continueKey),
    cancelText: t("cancel"),
    icon: <WarningIcon />,
    centered: true,
    onOk: onProceed,
    okButtonProps: confirmation.okButtonProps,
    cancelButtonProps: confirmation.cancelButtonProps,
    className: confirmation.swapButtonOrder ? "swap-modal-buttons" : undefined
  });
};

/**
 * MVP-28286: whether a program is Express Enroll in FN but NOT available for the patient
 * to complete in Patient Experience — sending such a program routes the patient to the
 * manufacturer's website instead of a PE Express Enroll form.
 *
 * Both flags come from the potential-savings payload: the backend derives hasPeExpressEnroll
 * from the active platform='PE' eeconfiguration row and the site's PE express-enrollment
 * setting (getPotentialSavings.js), so PE availability is data — not a template list.
 *
 * @param {Object} program - Potential saving object
 * @returns {boolean} True when the program's Express Enroll is FN-only
 */
export const isFnOnlyExpressEnroll = (program) => !!program?.hasExpressEnroll && !program.hasPeExpressEnroll;
